import React from 'react';
import { Database, AlertTriangle, Grid, TrendingUp } from 'lucide-react';

const MetricsCards = ({ summary }) => {
  const getQualityColor = (score) => {
    const numScore = typeof score === "string" ? parseFloat(score.replace("%", "")) : parseFloat(score);
    if (numScore >= 90) return "#10b981";
    if (numScore >= 70) return "#f59e0b";
    return "#ef4444";
  };

  const getQualityLabel = (score) => {
    const numScore = typeof score === "string" ? parseFloat(score.replace("%", "")) : parseFloat(score);
    if (numScore >= 90) return "Excellent";
    if (numScore >= 75) return "Good";
    if (numScore >= 60) return "Fair";
    if (numScore >= 40) return "Poor";
    return "Critical";
  };

  const calculateRotation = (score) => {
    const numScore = typeof score === "string" ? parseFloat(score.replace("%", "")) : parseFloat(score);
    return (numScore / 100) * 180 - 90;
  };

  const MetricCard = ({ icon: Icon, label, value, subtext, color = "blue" }) => (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 border-2 border-gray-100 hover:border-blue-200 hover:shadow-xl transition-all duration-300 group">
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 bg-gradient-to-br from-${color}-50 to-${color}-100 rounded-xl group-hover:scale-110 transition-transform duration-300`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
      <div className="space-y-1">
        <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
        <p className="text-3xl font-black text-gray-900">{value}</p>
        {subtext && <p className="text-xs text-gray-500 font-medium">{subtext}</p>}
      </div>
    </div>
  );

  if (!summary) return null;

  return (
    <div className="grid grid-cols-4 gap-6">
      {/* Speedometer Card */}
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 border-2 border-gray-100 hover:border-blue-200 hover:shadow-xl transition-all duration-300">
        <div className="speedometer-container">
          <svg viewBox="0 0 200 120" className="speedometer-svg">
            {/* Background arc */}
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              fill="none"
              stroke="#e2e8f0"
              strokeWidth="20"
              strokeLinecap="round"
            />
            {/* Colored segments */}
            <path d="M 20 100 A 80 80 0 0 1 56 36" fill="none" stroke="#dc2626" strokeWidth="20" strokeLinecap="round" />
            <path d="M 56 36 A 80 80 0 0 1 100 20" fill="none" stroke="#ea580c" strokeWidth="20" strokeLinecap="round" />
            <path d="M 100 20 A 80 80 0 0 1 144 36" fill="none" stroke="#eab308" strokeWidth="20" strokeLinecap="round" />
            <path d="M 144 36 A 80 80 0 0 1 180 100" fill="none" stroke="#16a34a" strokeWidth="20" strokeLinecap="round" />
            {/* Needle */}
            <line
              x1="100" y1="100" x2="100" y2="30"
              stroke={getQualityColor(summary.overall_quality_score)}
              strokeWidth="3"
              strokeLinecap="round"
              transform={`rotate(${calculateRotation(summary.overall_quality_score)} 100 100)`}
              className="speedometer-needle"
            />
            {/* Center dot */}
            <circle cx="100" cy="100" r="6" fill={getQualityColor(summary.overall_quality_score)} />
          </svg>
          <div className="speedometer-value">
            <div className="gauge-percentage">{summary.overall_quality_score}%</div>
            <div className="gauge-label">{getQualityLabel(summary.overall_quality_score)}</div>
          </div>
        </div>
      </div>

      {/* Other Metric Cards */}
      <MetricCard
        icon={Database}
        label="Total Records"
        value={summary.total_records.toLocaleString()}
        subtext="Records analyzed"
        color="blue"
      />
      <MetricCard
        icon={AlertTriangle}
        label="Issues Found"
        value={summary.total_issues || 0}
        subtext="Data quality issues"
        color="orange"
      />
      <MetricCard
        icon={Grid}
        label="Columns"
        value={summary.total_columns}
        subtext={`${summary.columns_with_issues || 0} with issues`}
        color="purple"
      />
    </div>
  );
};

export default MetricsCards;